/**
 * monitoring-perception-series-api.ts
 *
 * Builds the "Your AI Index" (perception) time series across ALL Brand
 * Monitoring runs for a brand, so the chart trends over every run instead of a
 * single point. The analytics_snapshots table the chart used to read collapses
 * same-day runs into one row, which is why the AI Index showed only the latest
 * run; this reads the runs directly via the monitoring history API.
 *
 * The AI Index is a 0–100 perception score that only Brand Monitoring produces
 * (Deep Signal runs measure the recommendation funnel, not perception), so this
 * series is sourced from monitoring runs. The funnel charts remain on the Deep
 * Signal series.
 */
import {
  fetchMonitoringHistory,
  fetchMonitoringRun,
} from "@/app/services/brand/monitoring-history-api";
import { normalizeProviderKey, isExcludedFromAnalytics } from "@/app/constants/models";

export interface PerceptionSeriesPoint {
  date: string;
  timestamp: number;
  overall: number;
  [modelKey: string]: number | string;
}

export interface MonitoringPerceptionSeries {
  selectedModels: string[];
  series: PerceptionSeriesPoint[];
  runCount: number;
}

const EMPTY: MonitoringPerceptionSeries = { selectedModels: [], series: [], runCount: 0 };

function num(v: any): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

async function buildSeries(brand: string): Promise<MonitoringPerceptionSeries> {
  if (!brand) return EMPTY;

  const history = await fetchMonitoringHistory(brand, 50);
  // Completed runs only, oldest-first for a left-to-right timeline.
  const runs = history
    .filter((r) => r.createdAt && (!r.status || r.status === "complete" || r.status === "completed"))
    .sort((a, b) => Date.parse(a.createdAt!) - Date.parse(b.createdAt!));
  if (!runs.length) return EMPTY;

  const loaded = await Promise.all(
    runs.map((r) => fetchMonitoringRun(r.id).catch(() => null)),
  );
  const pairs = runs
    .map((run, i) => ({ run, payload: loaded[i] }))
    .filter((p): p is { run: typeof runs[number]; payload: NonNullable<typeof loaded[number]> } => p.payload != null);
  if (!pairs.length) return EMPTY;

  // Model union across runs (excluding measurement-only lanes like gptweb).
  const modelSet = new Set<string>();
  for (const { payload } of pairs) {
    const mc = Array.isArray(payload.data?.modelComparison) ? payload.data.modelComparison : [];
    for (const item of mc) {
      const key = normalizeProviderKey(String(item?.model ?? ""));
      if (key && !isExcludedFromAnalytics(key)) modelSet.add(key);
    }
  }
  const selectedModels = [...modelSet];

  const series: PerceptionSeriesPoint[] = pairs.map(({ run, payload }) => {
    const data = payload.data ?? {};
    const ts = Date.parse(run.createdAt!);
    const date = new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const overall = num(data?.perceptionData?.overallScore);

    const byModel: Record<string, number> = {};
    const mc = Array.isArray(data?.modelComparison) ? data.modelComparison : [];
    for (const item of mc) {
      const key = normalizeProviderKey(String(item?.model ?? ""));
      if (!key || isExcludedFromAnalytics(key)) continue;
      byModel[key] = num(item?.score) || overall;
    }

    const point: PerceptionSeriesPoint = { date, timestamp: ts, overall };
    // Every point carries the full model set; a run missing a model holds at the
    // composite so a line never breaks.
    for (const m of selectedModels) point[m] = byModel[m] ?? overall;
    return point;
  });

  return { selectedModels, series, runCount: series.length };
}

// ── Short-lived cache (mirrors the Deep Signal series) ─────────────────────────
const _TTL_MS = 60_000;
const _cache = new Map<string, { at: number; p: Promise<MonitoringPerceptionSeries> }>();

if (typeof window !== "undefined") {
  window.addEventListener("analytics:refresh", () => _cache.clear());
}

export function fetchMonitoringPerceptionSeries(brand: string): Promise<MonitoringPerceptionSeries> {
  const key = (brand || "").toLowerCase();
  const now = Date.now();
  const hit = _cache.get(key);
  if (hit && now - hit.at < _TTL_MS) return hit.p;

  const p = buildSeries(brand).catch((err) => { _cache.delete(key); throw err; });
  _cache.set(key, { at: now, p });
  return p;
}
